import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';

import {isNil, omit} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useCloneServiceVehicleV2Mutation, useGetVehicleQuery} from '@dms/api/vehicle';
import {
  getServiceVehicleFieldAvailabilityContext,
  getServiceVehicleMandatorySchema,
  ServiceVehicleForm,
} from '@dms/features/service-vehicle-form';
import {
  buildBatteryCertificateFileFields,
  SERVICE_INTERVALS_DEFAULT_VALUES,
  ServiceVehicle,
  toAPIVehicle,
  toBaseVehicle,
  useFormNavigate,
  useGetCarFeatures,
  useVehicleWarrantySync,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {formatDocumentTitle, handleApiError, useMandatoryFields} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

const MANDATORY_FIELDS_OPTIONS = {
  definitionId: 'SERVICE',
  requiredStageId: 'SERVICE_ON_CREATION',
};

export function CloneServiceVehicle() {
  const {navigateOnDiscard, navigateOnConfirm} = useFormNavigate();
  const {id: vehicleId} = useRequiredParams();

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery({vehicleId}, {skip: isNilOrEmpty(vehicleId)});

  const [cloneServiceVehicleMutation, {isLoading: isCreating}] = useCloneServiceVehicleV2Mutation();

  const [getCarFeatures] = useGetCarFeatures();
  const {syncWarranties, isSyncing} = useVehicleWarrantySync();

  const {
    generateSchema,
    isLoading: isMandatoryFieldsLoading,
    isError: isMandatoryFieldsError,
    isMandatoryFieldsEnabled,
  } = useMandatoryFields(MANDATORY_FIELDS_OPTIONS);
  const formSchema = getServiceVehicleMandatorySchema(generateSchema);

  const handleSave = async (values: ServiceVehicle) => {
    const sourcingData = await getCarFeatures(values);
    const existingFeatures = vehicle?.features || null;
    const apiPayload = toAPIVehicle(values, existingFeatures, sourcingData);

    await cloneServiceVehicleMutation({
      cloneServiceVehicleV2RequestBody: {
        vehicle: apiPayload,
        source: {vehicleId, copyCustomFields: true},
        // The form does not prefill vehicle class from the source, so sending it unconditionally
        // would clear whatever the clone would otherwise inherit.
        ...(isNotNil(values.vehicleClassId) && {service: {vehicleClassId: values.vehicleClassId}}),
        additionalNote: values.additionalNote,
      },
      validateMandatoryFields: isMandatoryFieldsEnabled(
        undefined,
        getServiceVehicleFieldAvailabilityContext(values)
      ),
      fileFields: buildBatteryCertificateFileFields(values.batteryCertificateDocument),
    })
      .unwrap()
      .then(async (clonedVehicle) => {
        await syncWarranties({
          vehicleId: clonedVehicle.vehicleId,
          warranties: values.warranties ?? [],
        });
        navigateOnConfirm(clonedVehicle.vehicleId);
      })
      .catch(handleApiError);
  };

  const isLoading = isMandatoryFieldsLoading || isVehicleLoading;
  const isError = isVehicleError || isMandatoryFieldsError;

  const baseVehicle = isNotNil(vehicle)
    ? toBaseVehicle(omit(['registrationPlate', 'vin'], vehicle))
    : undefined;

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            i18n.t('entity.vehicle.labels.cloneServiceVehicle'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={isNil(baseVehicle)}
        emptyMessage={i18n.t('page.vehicle.notifications.notFound')}
        action={{
          title: i18n.t('general.actions.back'),
          leftIcon: 'navigation/chevron_left',
          onClick: navigateOnDiscard,
        }}
        data-testid="cloneServiceVehicle-notFound"
      >
        <VFKit.Form
          showMandatoryFields
          mandatoryFieldsOptions={MANDATORY_FIELDS_OPTIONS}
          experimentalZodSchema={formSchema}
          defaultValues={{
            ...baseVehicle,
            serviceIntervals: {
              ...SERVICE_INTERVALS_DEFAULT_VALUES,
              ...vehicle?.serviceIntervals,
            },
            warranties: [],
          }}
        >
          <ServiceVehicleForm
            onSave={handleSave}
            onDiscard={navigateOnDiscard}
            isCreating={isCreating || isSyncing}
            vehicleId={vehicleId}
            shouldShowWarranties
            tabRoutePattern="cloneServiceVehicleTabV2"
            modalKeys={{
              headline: i18n.t('entity.vehicle.labels.cloneServiceVehicle'),
              create: i18n.t('entity.vehicle.labels.clone'),
            }}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
